import { LoanParams } from 'app/models/loan-params'
import { ServicingPrincipalPaymentTransactionType } from 'app/models/dropdown-options'
import moment, { Moment } from 'moment'
import { EntityParams } from 'app/models/entity-params'
import {
  EntityProfileParams,
  EntityRole,
} from 'app/models/entity-profile-params'
import { Option } from 'app/models/dropdown-options'
import { currencyOptions } from 'app/models/dropdown-options'

export function isNumeric(str: string) {
  return /^[0-9]+$/.test(str)
}

export function isAlphanumeric(str: string) {
  return /^[a-zA-Z0-9]+$/.test(str)
}

export function isValidEmail(email: string) {
  if (typeof email != 'string') return false // we only process strings!
  return email.match(
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  )
}

export const numberOfDaysBetweenTwoDates = (startDate: any, endDate: any) => {
  const Difference_In_Time = endDate.getTime() - startDate.getTime()
  const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24)
  return Difference_In_Days
}

export const getDayBasis = (dayBasis: string) => {
  return Number(nextStringByDelimiter('_', dayBasis))
}

export const countDecimals = (value: number) => {
  const text = value.toString()
  // verify if number 0.000005 is represented as "5e-6"
  if (text.indexOf('e-') > -1) {
    const [, trail] = text.split('e-')
    const deg = parseInt(trail, 10)
    return deg
  }
  // count decimals for number in representation like "0.123456"
  if (Math.floor(value) !== value) {
    return value.toString().split('.')[1].length || 0
  }
  return 0
}

export function nextWordMatch(_preceedingWord: string, _text: string) {
  const words = _text.split(' ')
  const wordIndex = words.findIndex(word => word == _preceedingWord)
  return words[wordIndex + 1]
}

export const nextStringByDelimiter = (delimiter: string, _text: string) => {
  const words = _text.split(delimiter)
  return words[1]
}

export function roundTo(num: number, rounding: string) {
  switch (rounding) {
    case 'Five':
      return Number(num).toFixed(5)
    case 'NearestFourth':
      return roundToNearest(num, 0.25).toFixed(2)
    case 'UpwardFourth':
      return roundUpTo(Number(num), 0.25).toFixed(2)
    case 'DownwardFourth':
      return roundDownTo(Number(num), 0.25).toFixed(2)
    case 'NearestEighth':
      return roundToNearest(Number(num), 0.125).toFixed(3)
    case 'UpwardEighth':
      return roundUpTo(Number(num), 0.125).toFixed(3)
    case 'DownwardEighth':
      return roundDownTo(Number(num), 0.125).toFixed(3)
    case 'NearestSixteenth':
      return roundToNearest(Number(num), 0.0625).toFixed(4)
    case 'UpwardSixteenth':
      return roundUpTo(Number(num), 0.0625).toFixed(4)
    case 'DownwardSixteeth':
      return roundDownTo(Number(num), 0.0625).toFixed(4)
    case 'NearestTenth':
      return roundToNearest(Number(num), 0.1).toFixed(1)
    case 'UpwardTenth':
      return roundUpTo(Number(num), 0.1).toFixed(1)
    case 'DownwardTenth':
      return roundDownTo(Number(num), 0.1).toFixed(1)
    case 'NearestHundredth':
      return roundToNearest(Number(num), 0.01).toFixed(2)
    case 'UpwardHundredth':
      return roundUpTo(Number(num), 0.01).toFixed(2)
    case 'DownwardHundredth':
      return roundDownTo(Number(num), 0.01).toFixed(2)
    case 'NoRounding':
      return Number(num)
    default:
      // An unset or unknown mode must not turn the rate into NaN downstream.
      return Number(num)
  }
}

// Snap a rate to a step in integer arithmetic. Dividing floats directly puts
// on-step rates one step off (0.07 / 0.01 = 7.000000000000001, so ceil gives 8;
// 2.3 / 0.1 = 22.999999999999996, so floor gives 22). Rates carry at most five
// decimals and steps at most four, so scaling both by 1e6 makes every operand
// an exact integer and the division exact. This mirrors the ledger, which does
// the same division on an exact Decimal (RateRounding.daml).
const RATE_SCALE = 1e6
const toScaled = (n: number) => Math.round(n * RATE_SCALE)

const roundToNearest = (number: number, _roundTo: number) => {
  const step = toScaled(_roundTo)
  return (Math.round(toScaled(number) / step) * step) / RATE_SCALE
}

const roundUpTo = (number: number, _roundTo: number) => {
  const step = toScaled(_roundTo)
  return (Math.ceil(toScaled(number) / step) * step) / RATE_SCALE
}

const roundDownTo = (number: number, _roundTo: number) => {
  const step = toScaled(_roundTo)
  return (Math.floor(toScaled(number) / step) * step) / RATE_SCALE
}

export function decimalPlaces(num: any) {
  const match = ('' + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/)
  if (!match) {
    return 0
  }
  return Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0) -
      // Adjust for scientific notation.
      (match[2] ? +match[2] : 0),
  )
}

export function addMonths(dt: Date, n: number) {
  return new Date(dt.setMonth(dt.getMonth() + n))
}

export function numberWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// Safe value for a Mantine NumberInput that has `precision` set: Mantine calls
// .toFixed() on the value during render, which throws for anything that is not
// a number — and DAML serializes Decimal fields as strings, so API-loaded
// values arrive as strings. Returns a number, or '' for null/undefined/blank
// and unparseable input (Mantine's empty state).
export function toNumberInputValue(value: unknown): number | '' {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  const num = Number(value)
  return Number.isFinite(num) ? num : ''
}

// An FX rate is always optional — a loan without one simply shows no converted
// utilization. Only a rate the user actually typed is checked. A cleared
// Mantine NumberInput yields '' (and Number('') is 0), so blank has to be
// recognised before the comparison or an untouched field reads as invalid.
export function validateOptionalFxRate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const num = Number(value)
  if (!Number.isFinite(num)) {
    return null
  }
  return num > 0 ? null : 'FX rate must be greater than 0.'
}

// Normalizes an FX rate for the ledger, which wants a number or nothing at all.
// A cleared Mantine NumberInput yields '', which would otherwise be sent as an
// empty string.
export function toOptionalFxRate(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

export function formatDateToUTC(date: Date) {
  return moment(date).format('YYYY-MM-DD')
}

export function formatDateToMDY(date: Date) {
  return moment(date).format('MM-DD-YYYY')
}

export function stringToDate(date: string): Date {
  if (!date || date === '' || isNaN(new Date(date + 'T00:00:00').getTime())) {
    const newDate = new Date()
    newDate.setHours(0, 0, 0, 0)
    return newDate
  }
  return new Date(date + 'T00:00:00')
}

export const calculateInterestPaymentScheduler = (
  facilityFrequency: string | undefined | null,
  loan: LoanParams | undefined,
  businessDate: Date,
): { paymentDate: string; status: string }[] => {
  const periodMap: Record<string, number> = {
    Monthly: 1,
    Quarterly: 3,
    Semi: 6,
    Annual: 12,
  }

  const period: number = periodMap[facilityFrequency ?? 'Monthly'] ?? 1
  const resultCalculation: { paymentDate: string; status: string }[] = []
  let paymentDate: Date = loan?.nextInterestPaymentDate
    ? stringToDate(loan.nextInterestPaymentDate)
    : new Date()
  const loanEndDate: Date = loan?.endDate ? stringToDate(loan.endDate) : new Date()

  while (paymentDate <= loanEndDate) {
    resultCalculation.push({
      paymentDate: paymentDate.toDateString(),
      status: businessDate > paymentDate ? 'Past_Due' : 'Active',
    })
    paymentDate = addMonths(paymentDate, period)
  }
  resultCalculation.push({
    paymentDate: loanEndDate.toDateString(),
    status: 'Active',
  })

  return resultCalculation
}

export const calculatePikScheduler = (
  facilityFrequency: string | undefined | null,
  firstPikDate: string | Date | undefined,
  endDate: Date,
  businessDate: Date,
): { pikDate: string; status: string }[] => {
  const periodMap: Record<string, number> = {
    Monthly: 1,
    Quarterly: 3,
    Semi: 6,
    Annual: 12,
  }

  const period: number = periodMap[facilityFrequency ?? 'Monthly'] ?? 1
  const resultCalculation: { pikDate: string; status: string }[] = []
  let pikDate: Date = firstPikDate
    ? typeof firstPikDate === 'string'
      ? stringToDate(firstPikDate)
      : firstPikDate
    : new Date()

  while (pikDate < endDate) {
    resultCalculation.push({
      pikDate: pikDate.toDateString(),
      status: businessDate > pikDate ? 'Past_Due' : 'Scheduled',
    })
    pikDate = addMonths(pikDate, period)
  }

  return resultCalculation
}

export const formatNumberToCurrency = (
  value: number, // The numeric value to be formatted as currency.
  currencyCode: string, // The currency code for the desired currency format (i.e. 'USD', 'EUR').
  maximumFractionDigits?: number, // optional - The maximum number of fraction digits to use.
  minimumFractionDigits?: number, // optional - The minimum number of fraction digits to use.
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact', // optional - The notation to use for formatting the number.
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits,
    notation: notation,
  }).format(value)
}

export const formatToIsoCurrencyFormat = (
  value: number,
  currencyCode: string,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number,
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact',
): string => {
  const formattedNumber = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits,
    notation: notation,
  }).format(value)
  return `${currencyCode} ${formattedNumber}`
}

export function camelCaseToHumanReadable(input: string): string {
  // Add a space before each uppercase letter, then capitalize the first letter of each word
  return input
    .replace(/([A-Z])/g, ' $1') // Add space before each uppercase letter
    .replace(/^./, str => str.toUpperCase()) // Capitalize the first letter of the first word
    .split(' ') // Split the string into words
    .join(' ') // Join the words with a space
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function cleanErrorMessage(message: string) {
  return message.replace(/'(\w+)'/g, (_, fieldName) => {
    const readableFieldName = fieldName
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase letters
      .replace(/([a-zA-Z])(\d)/g, '$1 $2') // Add space between letters and numbers
    return `'${readableFieldName}'`
  })
}

export function getTransactionLabel(
  transactionValue: string,
  isInterestPaid: boolean,
): string {
  const updatedTransactionValue =
    transactionValue === 'InterestPayment'
      ? transactionValue
      : transactionValue + (isInterestPaid ? 'WithInterest' : '')

  const transaction = ServicingPrincipalPaymentTransactionType.find(
    item => item.value === updatedTransactionValue,
  )

  return transaction ? transaction.label : 'Unknown Payment Type'
}

// Create a map from entity IDs to entity names
export function mapEntityIdsToNames(entities: EntityParams[]) {
  const entityIdToName = new Map<string, string>()
  if (Array.isArray(entities)) {
    for (const entity of entities) {
      if (entity.id) {
        entityIdToName.set(entity.id, entity.entityName)
      }
    }
  }
  return entityIdToName
}

export function getLabelByValue(value: string, options: Option[]) {
  const option = options.find(opt => opt.value === value)
  return option ? option.label : ''
}

export const formatDateSnapshot = (effectiveDate?: Date) => {
  const now = effectiveDate ? effectiveDate : new Date()
  const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' }) // E.g., "Friday"
  const monthDayYear =
    now.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) +
    ', ' +
    now.getFullYear() // E.g., "July 23, 2023"

  return { dayOfWeek, monthDayYear }
}

export const formatDateToShow = (date?: Date | null) => {
  if (!date) return ''
  return moment(date).format('DD-MMM-YYYY')
}

export const formatDateTimeToShow = (date?: Date | null) => {
  if (!date) return ''
  return moment(date).format('DD-MMM-YYYY, HH:mm A')
}

export const formatDateTimeLong = (date?: Date | null) => {
  if (!date) return ''
  return moment(date).format('MMMM D, YYYY h:mm A')
}

export function getCurrencyDescription(
  currencyCode: string,
): string | undefined {
  const currency = currencyOptions.find(({ value }) => value === currencyCode)
  return currency?.label.split('-')[0].trim()
}

export function getDirection(amount: number): 'up' | 'down' | 'none' {
  if (amount > 0) return 'up'
  if (amount < 0) return 'down'
  return 'none'
}

export const sortData = <T>(
  data: T[],
  sortBy: keyof T | string,
  order: 'Ascending' | 'Descending' = 'Ascending',
): T[] => {
  return [...data].sort((a: any, b) => {
    if (!Object.keys(a).includes(sortBy as string)) {
      return 0 // Ignore invalid sortBy fields
    }

    const aValue = a[sortBy as keyof T]
    const bValue = b[sortBy as keyof T]

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue)
      return order === 'Ascending' ? comparison : -comparison
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'Ascending' ? aValue - bValue : bValue - aValue
    }

    return 0
  })
}

export function filterByTradeDate(
  value: [Date | null, Date | null] | [],
  tradeDate: string | Date,
): boolean {
  if (!Array.isArray(value) || value.length !== 2) return true
  const [startDate, endDate] = value
  if (!startDate && !endDate) return true

  const target = moment(tradeDate)

  // Treat a partially-picked or same-day selection as a single-day filter rather
  // than letting `moment(null)` produce an Invalid Date that filters everything out.
  if (startDate && !endDate) return target.isSame(moment(startDate), 'day')
  if (!startDate && endDate) return target.isSame(moment(endDate), 'day')

  return (
    target.isSameOrAfter(moment(startDate), 'day') &&
    target.isSameOrBefore(moment(endDate), 'day')
  )
}

export function toTitleCase(str: string) {
  return str
    .split(/(?=[A-Z])|_/)
    .join(' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase())
}

export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  fileName: string,
): void {
  if (!data.length) {
    return
  }

  // Extract headers from object keys, filter out unwanted columns
  const headers = Object.keys(data[0]).filter(
    key => key !== 'agencyAdmin' && key !== 'observers',
  )

  // Map rows by joining values in CSV format
  const rows = data.map(item =>
    headers
      .map(header => `"${(item[header] ?? '').toString().replace(/"/g, '""')}"`)
      .join(','),
  )

  // Create CSV content
  const csvContent = [headers.join(','), ...rows].join('\n')

  // Create and download the CSV file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()

  // Cleanup
  URL.revokeObjectURL(url)
}

export const getMyEntities = (
  entities: EntityParams[],
  entityProfiles: EntityProfileParams[],
): EntityParams[] => {
  // Get IDs for profiles where `isMyCompany` is true
  const myEntityProfileIds = entityProfiles
    .filter(profile => profile.isMyCompany)
    .map(profile => profile.id)

  // Filter entities based on the profile IDs
  const myEntities = entities.filter(entity =>
    entity.entityProfile.some(profile =>
      myEntityProfileIds.includes(profile.id),
    ),
  )
  // return sorted my entities
  return myEntities.sort((a, b) => a.entityName.localeCompare(b.entityName))
}

export const getLenderEntities = (
  entities: EntityParams[],
  entityProfiles: EntityProfileParams[],
): EntityParams[] => {
  // Get IDs for profiles where 'roles' includes 'LenderRole'
  const lenderProfileIds = entityProfiles
    .filter(profile => profile.roles.includes(EntityRole.LenderRole))
    .map(profile => profile.id)

  // Filter entities based on the profile IDs
  const lenderEntities = entities.filter(entity =>
    entity.entityProfile.some(profile => lenderProfileIds.includes(profile.id)),
  )
  // return sorted lender entities
  return lenderEntities.sort((a, b) => a.entityName.localeCompare(b.entityName))
}

export const getAdminAgentEntities = (
  entities: EntityParams[],
  entityProfiles: EntityProfileParams[],
): EntityParams[] => {
  // Get IDs for profiles where 'roles' includes 'AdminAgentRole'
  const adminAgentProfileIds = entityProfiles
    .filter(profile => profile.roles.includes(EntityRole.AdminAgentRole))
    .map(profile => profile.id)

  // Filter entities based on the profile IDs
  const adminAgentEntities = entities.filter(entity =>
    entity.entityProfile.some(profile =>
      adminAgentProfileIds.includes(profile.id),
    ),
  )
  // return sorted admin agent entities
  return adminAgentEntities.sort((a, b) =>
    a.entityName.localeCompare(b.entityName),
  )
}

export const getFundManagerParentEntities = (
  entities: EntityParams[],
  entityProfiles: EntityProfileParams[],
): EntityParams[] => {
  // Get IDs for profiles where 'roles' includes 'AdminAgentRole'
  const fundManagerProfileIds = entityProfiles
    .filter(profile => profile.roles.includes(EntityRole.FundManagerParentRole))
    .map(profile => profile.id)

  // Filter entities based on the profile IDs
  const fundManagerEntities = entities.filter(entity =>
    entity.entityProfile.some(profile =>
      fundManagerProfileIds.includes(profile.id),
    ),
  )
  // return sorted admin agent entities
  return fundManagerEntities.sort((a, b) =>
    a.entityName.localeCompare(b.entityName),
  )
}

export const getMyEntityFundManagers = (
  entities: EntityParams[],
  entityProfiles: EntityProfileParams[],
): EntityParams[] => {
  // Get my entities
  const myEntities = getMyEntities(entities, entityProfiles)

  // Get fund manager parent entities
  const fundManagerEntities = getFundManagerParentEntities(
    entities,
    entityProfiles,
  )

  // Filter my entities that are also fund manager/parent entities
  const myEntityFundManagers = myEntities.filter(entity =>
    fundManagerEntities.some(fundManager => fundManager.id === entity.id),
  )

  // Return sorted result
  return myEntityFundManagers.sort((a, b) =>
    a.entityName.localeCompare(b.entityName),
  )
}

// check if an entity is a Fund Manager (i.e. it has a profile with the role 'FundManagerParentRole')
export const isFundManagerEntity = (
  entityId: string | undefined,
  entities: EntityParams[],
  entityProfiles: EntityProfileParams[],
): boolean => {
  // Get IDs for profiles where 'roles' includes 'FundManagerParentRole'
  const fundManagerProfileIds = entityProfiles
    .filter(profile => profile.roles.includes(EntityRole.FundManagerParentRole))
    .map(profile => profile.id)

  // Find the entity by its ID
  const entity = entities.find(e => e.id === entityId)
  if (!entity) return false

  // Check if the entity's profiles contain any of the fund manager profile IDs
  return entity.entityProfile.some(profile =>
    fundManagerProfileIds.includes(profile.id),
  )
}

export function successFailureResponse(results: any) {
  return results.reduce(
    (result: any, value: any) => {
      if (value.ErrorMessage) {
        result.failure.push(value)
      } else {
        result.success.push(value)
      }
      return result
    },
    { success: [], failure: [] },
  )
}
export const formatPriceWithoutCurrency = (
  value: number,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number,
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits,
  }).format(value)
}

/**
 * Renders a stored price the way it was entered at trade creation, with no
 * padding to a fixed precision: `98.0` -> `98`, `98.7670000000` -> `98.767`,
 * `98.1234567891` unchanged.
 *
 * Deliberately string-only. The ledger round-trips price as a Numeric 10, and
 * routing it through a JS number to strip zeros would risk rounding the very
 * low-order decimals this is meant to preserve. Returns the input untouched if
 * it is not a plain decimal (no exponents, no separators).
 *
 * Accepts a bare trailing point (`98.` -> `98`) so that every value the caller's
 * Number.isFinite guard lets through is one this can normalise. The ledger emits
 * `98.0` rather than `98.`, so that form is defensive rather than observed.
 */
export const formatPriceAsEntered = (price: string): string => {
  const trimmed = price.trim()
  if (!/^-?(?:\d+\.\d*|\.\d+)$/.test(trimmed)) return trimmed
  const formatted = trimmed.replace(/0+$/, '').replace(/\.$/, '')
  // Trimming can consume the whole value: `.0` leaves ``, `-.0` leaves `-` and
  // `-0.0` leaves `-0`. Every such form is zero, so render it as one rather
  // than as a blank cell or a bare sign.
  return /^-?0*$/.test(formatted) ? '0' : formatted
}

export const getFeeSign = (paidTo: string): number => {
  if (paidTo.toLowerCase().includes('buyer')) {
    return -1
  } else if (paidTo.toLowerCase().includes('seller')) {
    return 1
  }
  return 0
}

export const computeFeeTotals = (
  fees: { feeAmount: string | number; currency: string; paidTo: string }[],
): Record<string, number> => {
  return fees.reduce(
    (acc, fee) => {
      const currency = fee.currency
      const feeAmount = parseFloat(fee.feeAmount + '')
      const sign = getFeeSign(fee.paidTo)
      const adjustedAmount = feeAmount * sign
      acc[currency] = (acc[currency] || 0) + adjustedAmount
      return acc
    },
    {} as Record<string, number>,
  )
}

export function businessDaysDiffWithHolidays(
  startMoment: Moment,
  endMoment: Moment,
  holidayDates: string[],
): number {
  let count = 0
  const holidaySet = new Set(holidayDates)
  const cur = startMoment.clone().add(1, 'days')

  while (cur.isSameOrBefore(endMoment, 'day')) {
    const dow = cur.day() // 0=Sun,6=Sat
    const iso = cur.format('YYYY-MM-DD')

    // Only Mon–Fri that aren’t in your holiday list
    if (dow !== 0 && dow !== 6 && !holidaySet.has(iso)) {
      count++
    }
    cur.add(1, 'days')
  }
  return count
}

export function addOneWorkingDay(
  startMoment: Moment,
  holidayDates: string[],
): Moment {
  const holidaySet = new Set(holidayDates)
  const cur = startMoment.clone().add(1, 'days')

  while (
    cur.day() === 0 ||
    cur.day() === 6 ||
    holidaySet.has(cur.format('YYYY-MM-DD'))
  ) {
    cur.add(1, 'days')
  }
  return cur
}

/**
 * Returns getDayProps callback for Mantine DateInput/DatePicker that highlights today's date.
 * Usage: <DateInput getDayProps={highlightToday()} />
 */
export const highlightToday = () => (date: Date) => {
  const today = new Date()
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()

  if (isToday) {
    return { style: { border: '1px solid #32439A' } }
  }
  return {}
}

export const downloadFromUrl = (url: string, fileName: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  // Firefox ignores clicks on anchors that aren't attached to the DOM
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
